/**
 * useConsent Hook
 *
 * Manages user consent state and localStorage persistence
 * Provides consent status and update functions
 *
 * User Story: US2 - Consent Management
 */

import { useState, useCallback, useEffect } from 'react';
import { CODEMERLIN_ANALYTICS_CONSENT_CHANGED } from '@/lib/analytics-consent-events';

/**
 * Consent record structure
 */
export interface ConsentRecord {
  consentGiven: boolean;
  timestamp: string;
  version: string;
  fingerprint?: string | undefined;
}

/**
 * useConsent Hook
 *
 * @returns Consent state and functions
 */
export function useConsent() {
  const [consent, setConsent] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const CONSENT_KEY = 'codemerlin_analytics_consent';
  const CONSENT_VERSION = '2024-12-21';

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const record: ConsentRecord = JSON.parse(stored);
        setConsent(record.consentGiven);
      } else {
        setConsent(null);
      }
    } catch (error) {
      console.error('Failed to load consent from localStorage:', error);
      setConsent(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Same-tab sync when ConsentBanner updates localStorage (`storage` only fires cross-tab).
  useEffect(() => {
    const syncFromStorage = (): void => {
      try {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored) {
          const record: ConsentRecord = JSON.parse(stored);
          setConsent(record.consentGiven);
        } else {
          setConsent(null);
        }
      } catch {
        setConsent(null);
      }
    };
    window.addEventListener(CODEMERLIN_ANALYTICS_CONSENT_CHANGED, syncFromStorage as EventListener);
    return (): void =>
      window.removeEventListener(
        CODEMERLIN_ANALYTICS_CONSENT_CHANGED,
        syncFromStorage as EventListener
      );
  }, []);

  /**
   * Update consent and persist to localStorage
   */
  const updateConsent = useCallback(
    (consentGiven: boolean, fingerprint?: string | undefined): void => {
      const record: ConsentRecord = {
        consentGiven,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION,
        fingerprint,
      };

      try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
        setConsent(consentGiven);
        window.dispatchEvent(new CustomEvent(CODEMERLIN_ANALYTICS_CONSENT_CHANGED));
      } catch (error) {
        console.error('Failed to save consent to localStorage:', error);
      }
    },
    []
  );

  /**
   * Clear consent (for testing/logout)
   */
  const clearConsent = useCallback((): void => {
    try {
      localStorage.removeItem(CONSENT_KEY);
      setConsent(null);
      window.dispatchEvent(new CustomEvent(CODEMERLIN_ANALYTICS_CONSENT_CHANGED));
    } catch (error) {
      console.error('Failed to clear consent:', error);
    }
  }, []);

  /**
   * Check if user has given consent
   */
  const hasConsent = useCallback((): boolean => {
    return consent === true;
  }, [consent]);

  /**
   * Check if user has declined consent
   */
  const hasDeclined = useCallback((): boolean => {
    return consent === false;
  }, [consent]);

  /**
   * Check if user decision is still undecided
   */
  const isUndecided = useCallback((): boolean => {
    return consent === null;
  }, [consent]);

  return {
    consent,
    isLoading,
    updateConsent,
    clearConsent,
    hasConsent,
    hasDeclined,
    isUndecided,
  };
}

export default useConsent;
